import { Badge, Box, Card, Flex, Heading, Text } from '@radix-ui/themes';
import { BallPreview } from 'components/common/ball-preview';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonVideoPreview } from 'components/common/video/preview';
import { PitchUploadStep } from 'enums/pitch-uploader';
import { t } from 'i18next';
import { VideoChangedFn } from 'interfaces/function-types';
import { ITVDefinition } from 'interfaces/traj-visualizer/i-definition';
import { GROUP_TARGET } from 'interfaces/traj-visualizer/i-group-definition';
import { DEFAULT_DELIVERY, DeliveryType } from 'lib_ts/enums/pitches.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { DEFAULT_SEAMS } from 'lib_ts/interfaces/pitches/i-base';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePitchUploaderStore } from 'stores/use-pitch-uploader-store';
import { useVideosStore } from 'stores/use-videos-store';

const COMPONENT_NAME = 'PitchUploaderSidebar';

export const PitchUploaderSidebar = () => {
  const step = usePitchUploaderStore((s) => s.step);
  const filename = usePitchUploaderStore((s) => s.filename);
  const format = usePitchUploaderStore((s) => s.format);
  const balls = usePitchUploaderStore((s) => s.balls);
  const activeID = usePitchUploaderStore((s) => s.activeID);
  const animateKey = usePitchUploaderStore((s) => s.animateKey);

  const activeBall = balls.find((m) => m._id === activeID);

  const [delivery, setDelivery] = useState<DeliveryType>(DEFAULT_DELIVERY);

  // reset delivery whenever active ball changes
  useEffect(() => {
    setDelivery(DEFAULT_DELIVERY);
  }, [activeID]);

  const handleVideoChange = useCallback<VideoChangedFn>(
    async (video_id) => {
      /** clear video details for active ball */
      if (!activeBall) {
        return;
      }

      const playback = await useVideosStore
        .getState()
        .lazyLoadPlayback(video_id);

      switch (delivery) {
        case DeliveryType.Windup: {
          activeBall.details.windupVideoID = video_id;
          activeBall.details.windupPlayback = playback;
          break;
        }

        case DeliveryType.Stretch: {
          activeBall.details.stretchVideoID = video_id;
          activeBall.details.stretchPlayback = playback;
          break;
        }

        default: {
          break;
        }
      }
    },
    [activeBall, delivery]
  );

  const trajModels = useMemo(() => {
    if (!activeBall?.bs) {
      return undefined;
    }

    if (!activeBall?.traj) {
      return undefined;
    }

    const output: ITVDefinition = {
      guid: 'main',
      description: 'common.target-trajectory-of-the-pitch',
      button_group: GROUP_TARGET,
      color: GROUP_TARGET.color,
      traj: activeBall.traj,
      bs: activeBall.bs,
    };
    return [output];
  }, [animateKey, activeBall?.bs, activeBall?.traj]);

  switch (step) {
    case PitchUploadStep.upload: {
      return (
        <ErrorBoundary identifier={COMPONENT_NAME}>
          <Card size="2">
            <Flex
              direction="column"
              align="center"
              justify="center"
              gap="4"
              minHeight="180px"
            >
              <Box>
                <Text color={RADIX.COLOR.SECONDARY}>
                  {t('pd.upload-file-msg')}
                </Text>
              </Box>
            </Flex>
          </Card>
        </ErrorBoundary>
      );
    }

    case PitchUploadStep.preview: {
      const blurbs: { label: string; text: string }[] = [];

      if (filename) {
        blurbs.push({
          label: 'common.file-name',
          text: filename,
        });
      }

      if (format) {
        blurbs.push({
          label: 'pd.detected-format',
          text: format,
        });
      }

      const validCount = balls.filter((m) => !m.fatalError).length;
      const invalidCount = balls.filter((m) => !!m.fatalError).length;

      blurbs.push({
        label: 'pd.valid-pitches',
        text: validCount.toFixed(0),
      });

      if (invalidCount > 0) {
        blurbs.push({
          label: 'pd.invalid-pitches',
          text: invalidCount.toFixed(0),
        });
      }

      return (
        <ErrorBoundary identifier={COMPONENT_NAME}>
          <Card size="2">
            <Flex direction="column" gap="4">
              {blurbs.map((b, i) => (
                <Box key={`upload-summary-${i}`}>
                  <Heading
                    mb="1"
                    size={RADIX.HEADING.SIZE.SM}
                    color={RADIX.COLOR.SECONDARY}
                  >
                    {t(b.label)}
                  </Heading>
                  <Badge>{b.text}</Badge>
                </Box>
              ))}
            </Flex>
          </Card>
        </ErrorBoundary>
      );
    }

    case PitchUploadStep.design: {
      if (!activeBall) {
        return null;
      }

      if (!trajModels) {
        return null;
      }

      const { bs, seams, details } = activeBall;

      if (!bs) {
        return null;
      }

      const videoID =
        delivery === DeliveryType.Windup
          ? details.windupVideoID
          : details.stretchVideoID;

      return (
        <ErrorBoundary identifier={COMPONENT_NAME}>
          <Flex
            direction="column"
            gap={RADIX.FLEX.GAP.MD}
            height="100%"
            className="rt-VertFlexColumn"
          >
            <Card size="2" className="rt-VertFlexGrow">
              <Flex direction="column" height="100%">
                <Heading size={RADIX.HEADING.SIZE.MD}>
                  {t('pd.ball-preview')}
                </Heading>

                <BallPreview
                  spin={bs}
                  orientation={seams ?? DEFAULT_SEAMS}
                  trajModels={trajModels}
                />
              </Flex>
            </Card>

            <Card size="2">
              <CommonVideoPreview
                identifier="PitchUploaderVideoPreview"
                previewPx={bs.px}
                previewPz={bs.pz}
                videoID={videoID}
                selectConfig={{
                  releasePx: bs.px,
                  onChange: handleVideoChange,
                }}
                delivery={delivery}
                onChangeDelivery={(v) => {
                  if (!activeBall) {
                    return;
                  }

                  setDelivery(v);
                }}
              />
            </Card>
          </Flex>
        </ErrorBoundary>
      );
    }

    default: {
      return null;
    }
  }
};
